import React, { useState, useRef } from 'react';
import { Modal, Form, Button, Message, Label, Icon, Input, Progress } from 'semantic-ui-react';
import './VideoLibrary.css';

const WARN_SIZE_BYTES = 500 * 1024 * 1024;

export function VideoUploadModal({ open, onClose, onUpload, uploading, uploadPhase, uploadProgress, uploadError }) {
    const [file, setFile] = useState(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [tags, setTags] = useState([]);
    const [tagInput, setTagInput] = useState('');
    const [localError, setLocalError] = useState('');
    const [sizeWarning, setSizeWarning] = useState('');
    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        const selected = e.target.files[0];
        if (!selected) return;
        setLocalError('');
        setSizeWarning(selected.size > WARN_SIZE_BYTES ? 'Warning: File is larger than 500 MB. Upload may take a while.' : '');
        setFile(selected);
    };

    const handleTagKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addTag();
        }
    };

    const addTag = () => {
        const trimmed = tagInput.trim().replace(/,$/, '');
        if (trimmed && !tags.includes(trimmed)) {
            setTags([...tags, trimmed]);
        }
        setTagInput('');
    };

    const removeTag = (tag) => {
        setTags(tags.filter((t) => t !== tag));
    };

    const handleSubmit = () => {
        if (!file) { setLocalError('Please select a video file.'); return; }
        if (!title.trim()) { setLocalError('Title is required.'); return; }
        setLocalError('');
        onUpload({ file, title: title.trim(), description: description.trim(), tags });
    };

    const handleClose = () => {
        setFile(null);
        setTitle('');
        setDescription('');
        setTags([]);
        setTagInput('');
        setLocalError('');
        setSizeWarning('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        onClose();
    };

    const displayError = localError || uploadError;

    return (
        <Modal open={open} onClose={handleClose} size="small">
            <Modal.Header>Upload Video</Modal.Header>
            <Modal.Content>
                {displayError && (
                    <Message negative>
                        <p>{displayError}</p>
                    </Message>
                )}
                {sizeWarning && (
                    <Message warning>
                        <p>{sizeWarning}</p>
                    </Message>
                )}
                {uploadPhase === 'uploading' && (
                    <Progress
                        percent={uploadProgress}
                        indicating
                        progress
                        label="Uploading video…"
                        className="video-upload-progress"
                    />
                )}
                <Form>
                    <Form.Field required>
                        <label>Video File</label>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="video/*"
                            onChange={handleFileChange}
                            aria-label="Video file"
                        />
                    </Form.Field>
                    <Form.Field required>
                        <label>Title</label>
                        <Input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Video title"
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Description</label>
                        <Form.TextArea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Optional description"
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Tags</label>
                        <Input
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyDown={handleTagKeyDown}
                            onBlur={addTag}
                            placeholder="Type a tag and press Enter or comma"
                        />
                        <div className="video-tags">
                            {tags.map((tag) => (
                                <Label key={tag} className="video-tag-label">
                                    {tag}
                                    <Icon name="delete" onClick={() => removeTag(tag)} className="video-tag-remove" />
                                </Label>
                            ))}
                        </div>
                    </Form.Field>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={handleClose} disabled={uploading}>Cancel</Button>
                <Button
                    primary
                    loading={uploading && uploadPhase !== 'uploading'}
                    disabled={uploading}
                    onClick={handleSubmit}
                >
                    {uploadPhase === 'preparing' && 'Preparing\u2026'}
                    {uploadPhase === 'uploading' && `Uploading\u2026 ${uploadProgress}%`}
                    {uploadPhase === 'saving' && 'Saving\u2026'}
                    {!uploadPhase && 'Upload'}
                </Button>
            </Modal.Actions>
        </Modal>
    );
}
